import { useContext, useEffect, useState } from "react";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import settingsIcon from "../../assets/settings.svg";
import CardIdIcon from "../../assets/cardId.svg";
import cardIcon from "../../assets/card.svg";
import wavesIcon from "../../assets/waves.svg";
import notificationOnIcon from "../../assets/notificationOn.svg";
import notificationOffIcon from "../../assets/notificationOff.svg";
import calculatorIcon from "../../assets/calculatorIcon.svg";
import helpIcon from "../../assets/helpIcon.svg";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import styles from "../../content/pages/homepage.module.css";
import { WidgetContext } from "src/contexts/WidgetContext";
import PaymentMethodSwipeable from "../PaymentMethod/PaymentMethodSwipeable";
import LogOutButton from "../LogComponents/LogOutButton";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteAccountButton from "../DeleteAccountButton/DeleteAccountButton";
import { useDoLogout } from "src/utils/useDoLogout";
import DeleteAccountDialog from "../DeleteAccountButton/DeleteAccountDialog";
import { AiSettingsDialog } from "../AiSettingsDialog/AiSettingsDialog";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteUser } from "src/api/deleteUser";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import PaymentButton from "../PaymentMethod/PaymentButton";
import HelpMenu from "../HelpMenu/HelpMenu";
import HelpMenuButton from "../HelpMenu/HelpMenuButton";
import AiButton from "../AiSettingsDialog/AiButton";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";

export default function QuanSettingsSpeedDial({
  onClose,
  isAiDialogOpen,
  onOpenAiSettings,
  onCloseAiSettings,
  onConfirmAiSettings,
  aiSettings,
  isUpdatingAiSettings,
  isPaymentOpen,
  onPaymentOpenChange,
  initialPaymentView,
  credits,
  enableCreditValidation,
}) {
  const [alert, setAlert] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const { openDrawer, isLoggedInAuth0ExperienceGlobal } = useContext(WidgetContext);

  // Close Help when Account Settings opens (sibling drawer; Help state lives here)
  useEffect(() => {
    if (isPaymentOpen) {
      setIsHelpDrawerOpen(false);
    }
  }, [isPaymentOpen]);

  //  Add state to control the delete dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 2. Get the logout function
  const doLogOut = useDoLogout();
  //Get the access token from Auth0 using the safe hook
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { getAccessTokenSilently } = useAuth0();

  //Function to toggle the speed dial
  const handleSettingsClick = () => {
    setIsOpen(!isOpen);
  };

  //Function to toggle alert state
  const handleClickAlert = () => {
    setAlert(!alert);
  };
  //Functions to open/close settingIcon
  // const handleOpen = () => setIsOpen(true);
  // const handleClose = () => setIsOpen(false);

  // Function to handle click on CardId icon
  const handleCardIdClick = () => {
    openDrawer();
  };

  // Function to handle click on icon
  const handleIconClick = (iconId: string) => {
    setSelectedIcon(iconId);
  };

  // 3.   Function that is executed when the user confirms the deletion
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      // 1. Get the access token from Auth0
      // const accessTokenFromAuth0 = await getAccessTokenSilently();
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();

      // 2. Call the API function to delete the user
      const isDeleted = await deleteUser(accessTokenFromAuth0);

      // 3. If the deletion was successful, logout
      if (isDeleted) {
        doLogOut();
      }
    } catch (error) {
      console.error("Failed to get access token for deletion", error);
    } finally {
      // 4. Always close the dialog, even if there is an error
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <SpeedDial
        ariaLabel="Settings Speed Dial"
        FabProps={{
          sx: {
            width: "30px !important",
            height: "30px !important",
            minWidth: "30px !important",
            minHeight: "30px !important",
          },
        }}
        sx={{
          // width: "40px",
          // height:  "40px",
          p: 0,
          bgcolor: "transparent",
          flexDirection: "row",
          pointerEvents: "auto",
          position: "relative",
          touchAction: "auto", // allow touch events
          zIndex: 14000,
          backgroundColor: "black", //this is the background color of the speed dial to avoid overlap with the text thoughts
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
          borderBottom: isOpen ? "none" : `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius: isOpen ? 0 : 20,
          borderBottomRightRadius: isOpen ? 0 : 20,
          "& .MuiSpeedDialAction-fab": {
            width: "30px",
            minWidth: "30px",
            height: "30px",
            minHeight: "30px",
            // Override MUI's staggered delay (30ms per action) so icons and background appear as one block
            transitionDelay: "0ms !important",
          },
          "& .MuiSpeedDial-actions": {
            backgroundColor: isOpen ? "black" : "transparent", //this is the background color of the speed dial to avoid overlap with the text thoughts
            borderBottomLeftRadius: 20,
            borderBottomRightRadius: 20,
            // Fade container in/out so background and icons are painted as one unit (avoids icons-first-then-background)
            opacity: isOpen ? 1 : 0,
            transition: "opacity 0.15s ease-out",
            // Like AudioSound: real border left/right/bottom only (no top). box-sizing + width/left so no layout jump.
            ...(isOpen && {
              boxSizing: "border-box",
              width: "32px",
              left: "-1px",
              borderLeft: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
              borderRight: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
              borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
            }),
            padding: "0px",
            position: "absolute",
            gap: 0,
            // right: "100%",
            top: "100%",
            // left: "40%",
            // transform: "translateX(-40%)",
            marginTop: "0px",
            paddingRight: "0",
            paddingTop: "0px",
            paddingBottom: "5px",
            // paddingRight: "30px",
            display: "flex",
            // flexDirection: "row-reverse",
            flexDirection: "column",
            touchAction: "auto", // allow touch events in the actions
            pointerEvents: "auto", // allow pointer events in the actions
          },
        }}
        direction="down"
        onOpen={() => setIsOpen(true)} // This is the function that opens the speed dial whit hover
        onClose={() => setIsOpen(false)} // the same but for close
        onClick={handleSettingsClick}
        open={isOpen}
        icon={<img src={settingsIcon} alt="Settings" className={`${styles.iconsLombard} iconWrapper ${isOpen ? styles.rotatedLeft : styles.rotatedRight}`} />}
      >
        {/* <SpeedDialAction
          key="alert"
          onClick={() => {
            handleClickAlert();
            handleIconClick("alert");
          }}
          icon={
            <img
              src={alert ? notificationOffIcon : notificationOnIcon}
              alt="Alert"
              className={`${styles.iconsLombard} iconWrapper`}
              style={{ filter: selectedIcon === "alert" ? "invert(50%) sepia(100%) saturate(500%) hue-rotate(330deg)" : "none" }}
            />
          }
          tooltipTitle=""
        /> */}

        {/* <SpeedDialAction
          key="Waves"
          onClick={() => handleIconClick("waves")}
          icon={
            <img
              src={wavesIcon}
              alt="Waves"
              className={`${styles.iconsLombard} iconWrapper`}
              style={{ filter: selectedIcon === "waves" ? "invert(50%) sepia(100%) saturate(500%) hue-rotate(330deg)" : "none" }}
            />
          }
          tooltipTitle=""
        /> */}

        {/* <SpeedDialAction
          key="CardId"
          onClick={() => handleIconClick("CardId")}
          icon={
            <img
              src={CardIdIcon}
              alt="Card Id"
              className={`${styles.iconsLombard} iconWrapper`}
              // style={{ filter: selectedIcon === "CardId" ? "invert(50%) sepia(100%) saturate(500%) hue-rotate(330deg)" : "none" }}
            />
          }
          tooltipTitle=""
        /> */}
        {/* <SpeedDialAction
          key="Calculator"
          onClick={() => handleIconClick("Calculator")}
          icon={
            <CurrencyExchangeIcon
              sx={{ fontSize : "24px" }}
              className={`${styles.iconsLombard} iconWrapper`}
              style={{ filter: selectedIcon === "Calculator" ? "invert(50%) sepia(100%) saturate(500%) hue-rotate(330deg)" : "none" }}
            />
          }
          tooltipTitle=""
        /> */}

        {/* Help icon */}
        <SpeedDialAction
          onClick={() => {
            handleIconClick("Help");
            onPaymentOpenChange?.(false); // Close Account Settings when Help opens
            onCloseAiSettings(); // Close AI settings when Help menu opens
            setIsHelpDrawerOpen(true);
          }}
          key="Help"
          icon={<HelpMenuButton />}
          className={`${styles.iconsLombard} iconWrapper`}
        />


        {/* AI settings action */}
        <SpeedDialAction
          key="ai"
          onClick={() => {
            handleIconClick("Ai");
            setIsDeleteDialogOpen(false);
            onPaymentOpenChange?.(false); // Close Account Settings when AI settings open
            setIsHelpDrawerOpen(false); // Close Help menu when AI settings open
            onOpenAiSettings();
          }}
          icon={
            <AiButton
            />
          }
          tooltipTitle=""
        />

      </SpeedDial>


      {/* Render the AI settings dialog */}
      <AiSettingsDialog open={isAiDialogOpen} onClose={onCloseAiSettings} onConfirm={onConfirmAiSettings} enabledSettings={aiSettings} isLoading={isUpdatingAiSettings} />

      {/* Render Help Get Started drawer (controlled from here) */}
      <HelpMenu
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}
        showButton={false}
      />
    </>
  );
}
